import { isValidISODateString } from "@/lib/businessRules/settlementDateRules";
import { fetchRequestWrapper } from "@/lib/utilities";
import holidayCalendarStore, {
  HolidayCheckStatus,
  IHolidayCheck,
} from "./holidayCalendarStore";

// LQC-2985 (spec B15) - the B14 holiday check. One date and one calendar per request,
// cached for the session; a holiday list does not change while a ticket is open.

export interface IHoliday {
  calendarCode: string;
  calendarName: string;
}

export function returnHolidayCheckKey(calendarCode: string, date: string): string {
  return `${calendarCode}|${date}`;
}

export function returnHolidayCheckEndpoint(calendarCode: string, date: string): string {
  return `/Calendars/${encodeURIComponent(calendarCode)}/holidays/${date}`;
}

// { isHoliday, date, calendarCode, calendarName }. null for anything else.
export function extractHolidayCheck(
  response: any,
): Pick<IHolidayCheck, "isHoliday" | "calendarName"> | null {
  if (typeof response?.isHoliday !== "boolean") return null;
  return {
    isHoliday: response.isHoliday,
    calendarName: typeof response.calendarName === "string" ? response.calendarName : "",
  };
}

// The calendars, among those asked about, on which the date is a holiday.
export function returnHolidaysForDate(
  holidayChecksByKey: Record<string, IHolidayCheck>,
  date: string,
  calendarCodes: string[],
): IHoliday[] {
  return calendarCodes.flatMap((calendarCode) => {
    const check = holidayChecksByKey[returnHolidayCheckKey(calendarCode, date)];
    if (check?.status !== HolidayCheckStatus.Loaded || !check.isHoliday) return [];
    return [{ calendarCode, calendarName: check.calendarName }];
  });
}

// "Holiday: London (GBLO)" - what the highlighted date input says on hover.
export function returnHolidayMessage(holidays: IHoliday[]): string {
  if (holidays.length === 0) return "";
  const names = holidays.map(({ calendarCode, calendarName }) =>
    calendarName ? `${calendarName} (${calendarCode})` : calendarCode,
  );
  return `Holiday: ${names.join(", ")}`;
}

// Fire-and-forget. A failure is cached too, so a down API toasts once, not per keystroke.
export async function checkHoliday(
  calendarCode: string,
  date: string,
  getAccessTokenSilently: Function,
): Promise<void> {
  if (!calendarCode || !isValidISODateString(date)) return;
  const key = returnHolidayCheckKey(calendarCode, date);
  const store = holidayCalendarStore.getState();
  if (store.holidayChecksByKey[key]) return;

  store.setHolidayCheck(key, {
    status: HolidayCheckStatus.Loading,
    isHoliday: false,
    calendarName: "",
  });
  try {
    const response = await fetchRequestWrapper(
      { url: returnHolidayCheckEndpoint(calendarCode, date) },
      getAccessTokenSilently,
    );
    const check = extractHolidayCheck(response);
    store.setHolidayCheck(
      key,
      check
        ? { status: HolidayCheckStatus.Loaded, ...check }
        : { status: HolidayCheckStatus.Error, isHoliday: false, calendarName: "" },
    );
  } catch {
    store.setHolidayCheck(key, {
      status: HolidayCheckStatus.Error,
      isHoliday: false,
      calendarName: "",
    });
  }
}
